/**
 * Cupom de FECHAMENTO DE CAIXA — fundo branco e texto preto (TM-T20X).
 * Mesmo padrão HTML dos demais cupons (cupomFechamento, cupomPreparo, cupomDelivery).
 */
import { smartPrint } from "./printConnector";
import { fmtMoeda, buildLogoUrl, buildLogoHtml, getEmpresaData, getImpressaoConfig, buildEmpresaPayload, buildFontScaleStyle, getAllProductsCached } from "./cupomFormatters";
import { formatarQtd, medidaDoItem } from "../lib/unidadeVenda";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function fmtDateShort(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function fmtTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/** Linha com label à esquerda e valor à direita, pontilhado entre eles */
function rowHtml(label, valor, opts = {}) {
  const { prefix = "", bold = false, fontSize = "12px" } = opts;
  const style = `display:flex;justify-content:space-between;font-size:${fontSize};font-weight:${bold ? "900" : "bold"};${opts.extra || ""}`;
  return `<div style="${style}"><span>${prefix}${label}</span><span>R$ ${fmtMoeda(valor)}</span></div>`;
}

function rowText(label, value, opts = {}) {
  const { bold = false, fontSize = "12px" } = opts;
  const style = `display:flex;justify-content:space-between;font-size:${fontSize};font-weight:${bold ? "900" : "bold"};${opts.extra || ""}`;
  return `<div style="${style}"><span>${label}</span><span>${value}</span></div>`;
}

const escaparHtml = (t) => String(t ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/**
 * @param {Object} caixa - dados do caixa fechado (inclui conferencia)
 * @param {Array} movimentos - lista de movimentos
 * @param {Object} resumo - { vendas_por_pagamento, outros, vendas_por_modulo, estatisticas }
 */
export async function imprimirCupomCaixa(caixa, movimentos = [], resumo = null) {
  // Fetch company settings (com cache) e pedidos em PARALELO
  let company = { company_name: "Núcleo", fantasy_name: "", slogan: "", address: "", cnpj: "", logo_url: null };
  const tok = localStorage.getItem("token");
  const [companyData, pedidosRes, impressaoConfig, produtosCadastro] = await Promise.all([
    getEmpresaData(),
    fetch(`${API_BASE}/api/caixa/${caixa.id}/pedidos`, { headers: tok ? { Authorization: `Bearer ${tok}` } : {}, signal: AbortSignal.timeout(10000) }).then(r => r.json()).catch(() => []),
    getImpressaoConfig(),
    getAllProductsCached().catch(() => []),
  ]);
  if (companyData) company = { ...company, ...companyData };
  const pedidosCaixa = pedidosRes || [];
  const _fontScaleCss = buildFontScaleStyle(impressaoConfig.font_scale);

  const nomeEmpresa = company.company_name || company.fantasy_name || "Núcleo";
  const nomeOperador = caixa.usuario_nome || "—";
  const conferencia = caixa.conferencia || {};

  // Logo
  const logoUrl = buildLogoUrl(company);
  const logoHtml2 = buildLogoHtml(logoUrl);

  // === CALCULATIONS ===
  const abertura = caixa.valor_abertura || 0;
  const vendasPorPagamento = resumo?.vendas_por_pagamento || [];
  const outros = resumo?.outros || [];
  const totalSangrias = outros.find(o => o.tipo === "sangria")?.total || 0;
  const totalEntradas = outros.find(o => o.tipo === "adicao")?.total || 0;
  const vendasPorModulo = resumo?.vendas_por_modulo || [];
  const stats = resumo?.estatisticas || {};
  const totalPedidos = stats.total_pedidos || 0;
  const ticketMedio = stats.ticket_medio || 0;
  const subtotalVendas = vendasPorPagamento.reduce((acc, v) => acc + (v.total || 0), 0);
  const vendasDinheiro = vendasPorPagamento.find(v => (v.forma_pagamento || "").toLowerCase().includes("dinheiro"))?.total || 0;
  const dinheiroEsperado = abertura + vendasDinheiro - totalSangrias + totalEntradas;
  const movimentacoesTotal = subtotalVendas + abertura - totalSangrias + totalEntradas;
  // Estornos (v1.5.789): pedido cancelado depois de virar venda. As vendas por
  // forma já vêm líquidas; a linha abaixo só mostra quanto foi descontado.
  const totalEstornos = (resumo?.estornos || []).reduce((acc, e) => acc + (e.total || 0), 0);

  // Quilo/litro (30/09/2026): produto por peso conta 1 produto — 1,250 kg não
  // é "1,25 produtos" nem soma com unidade. A quantidade pode vir em `quantity`.
  const qtdProdutos = (items) => (items || []).reduce((a, i) =>
    a + (medidaDoItem(i, produtosCadastro) ? 1 : Number(i.quantidade || i.qty || i.quantity || 1)), 0);
  const fmtQtd = (n) => formatarQtd(Math.round(n * 1000) / 1000, null);

  // Cancelados
  const cancelados = pedidosCaixa.filter(p => p.status === "cancelado");
  const totalCancelados = cancelados.reduce((acc, p) => acc + (p.total || 0), 0);
  const qtdProdutosCancelados = cancelados.reduce((acc, p) => acc + qtdProdutos(p.items), 0);

  // Conferencia totals
  let conferenciaTotal = 0;
  let conferenciaEsperadoTotal = 0;

  // Media de produtos (1 casa quando quebrada: 2,5 em vez de arredondar para 3)
  let mediaProdutos = 0;
  if (totalPedidos > 0) {
    const media = pedidosCaixa.filter(p => p.status !== "cancelado").reduce((acc, p) => acc + qtdProdutos(p.items), 0) / totalPedidos;
    mediaProdutos = formatarQtd(Math.round(media * 10) / 10, null);
  }

  // === BUILD VENDAS POR MODULO ===
  let vendasModuloHtml = "";
  for (const vm of vendasPorModulo) {
    vendasModuloHtml += rowHtml(vm.modulo || "Outros", vm.total);
  }

  // === BUILD MOVIMENTACOES POR PAGAMENTO ===
  let movPagHtml = "";
  for (const vp of vendasPorPagamento) {
    const label = vp.forma_pagamento || "Outros";
    const isAuto = label.toLowerCase().includes("marketplace") || label.toLowerCase().includes("pix auto");
    if (isAuto) {
      movPagHtml += rowText(label, "AUTO");
    } else {
      movPagHtml += rowHtml(label, vp.total);
    }
  }

  // === BUILD CONFERENCIA ===
  let confHtml = "";
  // Conferência gravada no fechamento (v1.5.784): esperado e contado vêm do
  // servidor, com a gaveta sempre presente e as formas online como AUTO.
  const detalhe = Array.isArray(caixa.conferencia_detalhe) ? caixa.conferencia_detalhe : null;
  for (const f of detalhe || []) {
    conferenciaEsperadoTotal += Number(f.esperado) || 0;
    conferenciaTotal += Number(f.contado) || 0;
    const cel = (v) => (f.automatico ? "AUTO" : `R$ ${fmtMoeda(v)}`);
    const dif = Number(f.diferenca) || 0;
    confHtml += `<div style="display:flex;justify-content:space-between;font-size:11px;font-weight:bold;">
        <span style="flex:2;">${escaparHtml(f.forma)}</span>
        <span style="flex:1;text-align:right;">${cel(f.esperado)}</span>
        <span style="flex:1;text-align:right;">${cel(f.contado)}</span>
        <span style="flex:1;text-align:right;">${f.automatico ? "AUTO" : `${dif >= 0 ? "+" : ""}R$ ${fmtMoeda(dif)}`}</span>
      </div>`;
  }
  for (const vp of detalhe ? [] : vendasPorPagamento) {
    const label = vp.forma_pagamento || "Outros";
    const isAuto = label.toLowerCase().includes("marketplace") || label.toLowerCase().includes("pix auto");
    let esperado;
    if (label.toLowerCase().includes("dinheiro")) {
      esperado = dinheiroEsperado;
    } else {
      esperado = vp.total || 0;
    }
    const conferido = conferencia[label] || 0;
    const diferenca = conferido - esperado;
    conferenciaEsperadoTotal += esperado;
    conferenciaTotal += conferido;

    if (isAuto) {
      confHtml += `<div style="display:flex;justify-content:space-between;font-size:11px;font-weight:bold;">
        <span style="flex:2;">${label}</span>
        <span style="flex:1;text-align:right;">AUTO</span>
        <span style="flex:1;text-align:right;">AUTO</span>
        <span style="flex:1;text-align:right;">AUTO</span>
      </div>`;
    } else {
      confHtml += `<div style="display:flex;justify-content:space-between;font-size:11px;font-weight:bold;">
        <span style="flex:2;">${label}</span>
        <span style="flex:1;text-align:right;">R$ ${fmtMoeda(esperado)}</span>
        <span style="flex:1;text-align:right;">R$ ${fmtMoeda(conferido)}</span>
        <span style="flex:1;text-align:right;">${diferenca >= 0 ? "+" : ""}R$ ${fmtMoeda(diferenca)}</span>
      </div>`;
    }
  }
  const totalDif = conferenciaTotal - conferenciaEsperadoTotal;

  // === BUILD CANCELADOS ===
  let canceladosHtml = "";
  if (cancelados.length > 0) {
    for (const p of cancelados) {
      const codigo = (p.codigo || "#---").replace("#", "").padStart(5, "0");
      const modulo = (p.modulo || "—");
      const hora = fmtTime(p.created_at);
      const prods = fmtQtd(qtdProdutos(p.items));
      canceladosHtml += `<div style="display:flex;justify-content:space-between;font-size:11px;font-weight:bold;padding:1px 0;border-bottom:1px dotted #ccc;">
        <span>#${codigo}</span>
        <span>${modulo}</span>
        <span>${hora}</span>
        <span>${prods}</span>
        <span>R$ ${fmtMoeda(p.total)}</span>
      </div>`;
    }
  }

  // === FINAL HTML ===
  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Fechamento Caixa #${caixa.id}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  body { margin: 0; padding: 8px; font-family: "Courier New", monospace; font-size: 13px; font-weight: bold; background: none; color: #000; }
  .line { border-bottom: 1px dashed #000; margin: 6px 0; }
  .center { text-align: center; }
  .section { color: #000; font-weight: bold; margin: 4px 0; }
  .sep { text-align: center; font-size: 12px; margin: 4px 0; }
  @media print { body { max-width: 72.1mm; margin: 0 auto; } }
  ${_fontScaleCss}
</style>
</head>
<body style="max-width:72.1mm;margin:0 auto;">
<div><br><br><br></div>

${logoHtml2}
<div class="center" style="font-weight:bold;font-size:16px;">${nomeEmpresa}</div>
${(company.slogan || "").trim() ? `<div class="center" style="font-size:11px;font-style:italic;color:#333;margin-bottom:4px;">${company.slogan}</div>` : ""}
${(company.cnpj || "").trim() ? `<div class="center" style="font-size:11px;font-weight:bold;">CNPJ: ${company.cnpj}</div>` : ""}

<div class="center section" style="font-size:11px;letter-spacing:2px;margin-top:6px;">....... fechamento .......</div>
<div class="center" style="font-size:13px;font-weight:bold;">${nomeOperador}</div>
<div class="line"></div>

<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;">
  <span>ABERTURA: ${fmtDateShort(caixa.opened_at)}</span>
</div>
<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:bold;">
  <span>FECHAMENTO: ${fmtDateShort(caixa.closed_at)}</span>
</div>

<div class="line"></div>
<div class="center section" style="font-size:11px;letter-spacing:2px;">......... VENDAS .........</div>
<div class="line"></div>

${vendasModuloHtml}
${vendasPorModulo.length > 0 ? '<div class="sep">--------</div>' : ""}
${rowHtml("Subtotal", subtotalVendas)}
<div class="sep">--------</div>
${rowHtml("TOTAL", subtotalVendas, { bold: true, fontSize: "14px" })}

<div style="margin-top:8px;"></div>
<div class="center section" style="font-size:11px;letter-spacing:2px;">..... MOVIMENTAÇÕES .....</div>
<div class="line"></div>

${movPagHtml}
${totalEstornos > 0 ? rowHtml("  Estornos (já descontados)", -totalEstornos) : ""}
<div style="margin-top:4px;"></div>
${rowHtml("Subtotal", subtotalVendas)}
<div class="sep">--------</div>
${rowHtml("(=) Abertura", abertura)}
${rowHtml("(-) Sangrias", totalSangrias)}
${rowHtml("(+) Entradas", totalEntradas)}
<div style="margin-top:4px;"></div>
${rowHtml("Subtotal", movimentacoesTotal)}
<div class="sep">--------</div>
${rowHtml("TOTAL", movimentacoesTotal, { bold: true, fontSize: "14px" })}

<div style="margin-top:8px;"></div>
<div class="center section" style="font-size:11px;letter-spacing:2px;">..... ESTATÍSTICAS .....</div>
<div class="line"></div>

${rowText("Pedidos", totalPedidos)}
${rowHtml("Ticket Médio", ticketMedio)}
${totalPedidos > 0 ? rowText("Média de Produtos", mediaProdutos) : ""}
<div class="line"></div>

<div style="margin-top:8px;"></div>
<div class="center section" style="font-size:11px;letter-spacing:2px;">..... CONFERÊNCIA .....</div>
<div class="line"></div>

<div style="display:flex;justify-content:space-between;font-size:10px;font-weight:bold;color:#333;margin-bottom:4px;">
  <span style="flex:2;">Descrição</span>
  <span style="flex:1;text-align:right;">Líquido</span>
  <span style="flex:1;text-align:right;">Conf.</span>
  <span style="flex:1;text-align:right;">Dif.</span>
</div>
${confHtml}
<div class="sep">--------</div>
<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:900;">
  <span style="flex:2;">TOTAL</span>
  <span style="flex:1;text-align:right;">R$ ${fmtMoeda(conferenciaEsperadoTotal)}</span>
  <span style="flex:1;text-align:right;">R$ ${fmtMoeda(conferenciaTotal)}</span>
  <span style="flex:1;text-align:right;">${totalDif >= 0 ? "+" : ""}R$ ${fmtMoeda(totalDif)}</span>
</div>
${caixa.justificativa ? `<div style="font-size:10px;margin-top:4px;">Motivo da diferença: ${escaparHtml(caixa.justificativa)}</div>` : ""}

${cancelados.length > 0 ? `
<div style="margin-top:8px;"></div>
<div class="center section" style="font-size:11px;letter-spacing:2px;">..... CANCELADOS .....</div>
<div class="line"></div>
<div style="display:flex;justify-content:space-between;font-size:10px;font-weight:bold;color:#333;margin-bottom:4px;">
  <span>PEDIDO</span><span>MÓDULO</span><span>HORA</span><span>PROD</span><span>VALOR</span>
</div>
${canceladosHtml}
<div class="sep">--------</div>
<div style="display:flex;justify-content:space-between;font-size:12px;font-weight:900;">
  <span>TOTAL</span><span></span><span></span><span>${fmtQtd(qtdProdutosCancelados)}</span><span>R$ ${fmtMoeda(totalCancelados)}</span>
</div>
` : ""}

<div class="line"></div>
<div class="center" style="font-size:10px;color:#333;">${fmtDate(new Date().toISOString())}</div>
<div class="center" style="font-size:10px;font-weight:bold;color:#333;margin-top:6px;">NÃO É DOCUMENTO FISCAL</div>
<div class="center" style="font-size:10px;font-weight:bold;color:#333;margin-top:4px;">Sistema NÚCLEO - O Centro da sua gestão</div>
<div><br><br><br></div>
</body></html>`;

  // Impressoras (com cache compartilhado)
  const printerNames = impressaoConfig.caixa || impressaoConfig.fechamento || [];

  const empresaData = buildEmpresaPayload(company, logoUrl);
  return smartPrint(html, "CAIXA", printerNames, null, empresaData, "Caixa");
}
